/**
 * TeleTunes Product Video Autoplay
 * - Auto-triggers video playback when the video slide scrolls into view
 * - Unmutes after playback starts (falls back to muted if browser blocks)
 * - Click video to pause/play (no zoom dialog for videos)
 * - Pauses when scrolled out of view
 */
(function () {
  const managed = new WeakSet();

  function manageVideoContainer(container) {
    if (managed.has(container)) return;
    managed.add(container);

    // Remove zoom click handler from video slides — we want click = pause/play, not zoom
    const slideEl = container.closest('slideshow-slide') || container;
    slideEl.removeAttribute('on:click');
    // Also remove from the container itself
    container.removeAttribute('on:click');
    // And any parent that might have it
    const zoomable = container.closest('[on\\:click*="zoom"]');
    if (zoomable) zoomable.removeAttribute('on:click');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(container);
          } else {
            onHidden(container);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(container);
  }

  function onVisible(container) {
    const dm = container.querySelector('deferred-media');
    if (!dm) return;

    // Step 1: If not loaded, click the theme's play button to trigger load
    if (!dm.getAttribute('data-media-loaded')) {
      const btn = dm.querySelector('.deferred-media__poster-button');
      if (btn) btn.click();
    }

    // Disable theme's own toggle button to prevent double-action
    disableThemeToggle(container);

    // Step 2: Once video is in DOM, ensure it loads and plays with sound
    pollVideo(container, 30, (video) => {
      // Force full preload
      if (video.preload === 'metadata' || video.preload === 'none') {
        video.preload = 'auto';
      }
      video.loop = true;

      // If already playing (theme autoplay kicked in), just unmute
      if (!video.paused) {
        tryUnmute(video);
        return;
      }

      // Listen for canplay then start
      const startPlay = () => {
        video.removeEventListener('canplay', startPlay);
        tryUnmute(video);
        video.play().catch(() => {});
      };

      if (video.readyState >= 3) {
        startPlay();
      } else {
        video.addEventListener('canplay', startPlay);
        if (video.readyState === 0) {
          video.load();
        }
      }
    });
  }

  function onHidden(container) {
    const video = container.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  function tryUnmute(video) {
    video.muted = false;
    setTimeout(() => {
      if (video.muted) {
        // Browser forced mute back — nothing we can do
      }
    }, 100);
  }

  function pollVideo(container, attempts, callback) {
    const video = container.querySelector('video');
    if (video) {
      callback(video);
    } else if (attempts > 0) {
      setTimeout(() => pollVideo(container, attempts - 1, callback), 150);
    }
  }

  function disableThemeToggle(container) {
    const toggleBtn = container.querySelector('.video-interaction-hint');
    if (toggleBtn) {
      toggleBtn.style.pointerEvents = 'none';
    }
  }

  // Click on video = toggle pause/play
  // Capture phase + stopImmediatePropagation blocks all theme handlers
  document.addEventListener(
    'click',
    (e) => {
      const vc = e.target.closest('.product-media-container--video');
      if (!vc) return;

      const dm = vc.querySelector('deferred-media');
      // Let initial play button work normally (before video is loaded)
      if (dm && !dm.getAttribute('data-media-loaded')) return;

      const video = vc.querySelector('video');
      if (!video) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (video.paused) {
        tryUnmute(video);
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      } else {
        video.pause();
      }
    },
    true
  );

  // Block zoom dialog from opening for video slides at the slideshow level too
  document.addEventListener(
    'click',
    (e) => {
      // If clicking inside a video container and zoom dialog would open, block it
      if (e.target.closest('.product-media-container--video')) {
        const dm = e.target
          .closest('.product-media-container--video')
          ?.querySelector('deferred-media');
        if (dm && dm.getAttribute('data-media-loaded')) {
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }
    },
    false
  );

  function init() {
    document
      .querySelectorAll('.product-media-container--video')
      .forEach(manageVideoContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
